import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { UserCheck, UserX, Pencil, Trash2, Printer, Plus, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AttendanceTab = ({
  activeEmployees,
  attendanceRecords,
  attendanceDate,
  setAttendanceDate,
  attendanceEndDate,
  setAttendanceEndDate,
  attendanceEmployee,
  setAttendanceEmployee,
  onClockIn,
  onClockOut,
  getEmployeeName,
  refreshAttendance,
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clockInVal, setClockInVal] = useState("");

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const handlePrintTimesheet = () => {
    if (!attendanceRecords || attendanceRecords.length === 0) {
      toast.warning("Nothing to print — no records in this range.");
      return;
    }
    // Group records by employee, preserving date order (earliest first)
    const byEmp = {};
    attendanceRecords.forEach((r) => {
      const eid = r.employee_id || "unknown";
      if (!byEmp[eid]) byEmp[eid] = [];
      byEmp[eid].push(r);
    });
    Object.values(byEmp).forEach((arr) =>
      arr.sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    );

    const empSections = Object.keys(byEmp).map((eid) => {
      const records = byEmp[eid];
      const totalHrs = records.reduce((s, r) => s + Number(r.hours_worked || 0), 0);
      const totalOT = records.reduce((s, r) => s + Number(r.overtime_hours || 0), 0);
      const daysWorked = records.filter((r) => r.clock_in).length;
      const rows = records
        .map((r) => {
          const d = r.date ? new Date(r.date + "T00:00:00") : null;
          const dayName = d
            ? d.toLocaleDateString("en-ZA", { weekday: "short" })
            : "";
          const status = r.is_leave
            ? `${r.leave_type || ""} leave`
            : r.clock_in
              ? r.clock_out
                ? "Complete"
                : "Working"
              : "—";
          return `<tr>
            <td>${escapeHtml(r.date || "")}</td>
            <td>${escapeHtml(dayName)}</td>
            <td>${escapeHtml(r.clock_in || "—")}</td>
            <td>${escapeHtml(r.clock_out || "—")}</td>
            <td class="num">${Number(r.hours_worked || 0).toFixed(2)}</td>
            <td class="num">${Number(r.overtime_hours || 0).toFixed(2)}</td>
            <td>${escapeHtml(status)}</td>
          </tr>`;
        })
        .join("");
      return `
        <section class="emp-block">
          <h2>${escapeHtml(getEmployeeName(eid))}</h2>
          <div class="emp-summary">
            <span><b>Total hours:</b> ${totalHrs.toFixed(2)}</span>
            <span><b>Overtime:</b> ${totalOT.toFixed(2)}</span>
            <span><b>Days worked:</b> ${daysWorked}</span>
            <span><b>Records:</b> ${records.length}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th class="num">Hours</th>
                <th class="num">Overtime</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="signature">
            <div class="sig-block">Employee signature</div>
            <div class="sig-block">Manager signature</div>
          </div>
        </section>
      `;
    });

    const grandHrs = attendanceRecords.reduce(
      (s, r) => s + Number(r.hours_worked || 0),
      0
    );
    const grandOT = attendanceRecords.reduce(
      (s, r) => s + Number(r.overtime_hours || 0),
      0
    );
    const empLabel =
      !attendanceEmployee || attendanceEmployee === "all"
        ? "All Employees"
        : getEmployeeName(attendanceEmployee);
    const start = attendanceDate || "";
    const end = attendanceEndDate || attendanceDate || "";
    const printedAt = new Date().toLocaleString("en-ZA");

    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) {
      toast.error("Pop-up blocked. Allow pop-ups for this site to print.");
      return;
    }
    win.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>Timesheet — ${escapeHtml(empLabel)} — ${escapeHtml(start)} to ${escapeHtml(end)}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; }
    h1 { font-size: 22px; margin: 0 0 4px 0; letter-spacing: 1px; }
    h2 { font-size: 16px; margin: 18px 0 6px 0; padding-top: 4px; border-top: 2px solid #000; }
    .sub { font-size: 12px; color: #555; margin-bottom: 14px; }
    .meta { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 12px; font-size: 13px; }
    .meta b { display: block; color: #000; }
    .grand { background: #f5f5f5; padding: 10px 12px; border-radius: 6px; margin-bottom: 8px; font-size: 13px; display: flex; gap: 18px; flex-wrap: wrap; }
    .grand b { font-size: 16px; }
    .emp-block { page-break-inside: avoid; margin-bottom: 18px; }
    .emp-summary { display: flex; gap: 18px; flex-wrap: wrap; font-size: 12px; color: #333; margin-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border-bottom: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 12px; }
    th { background: #f5f5f5; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .signature { margin-top: 24px; display: flex; gap: 30px; font-size: 11px; color: #555; }
    .sig-block { flex: 1; border-top: 1px solid #000; padding-top: 4px; min-height: 24px; }
    @media print { .no-print { display: none; } }
  </style>
</head>
<body>
  <h1>TIMESHEET</h1>
  <div class="sub">Generated ${escapeHtml(printedAt)}</div>
  <div class="meta">
    <div><b>Period</b>${escapeHtml(start)} → ${escapeHtml(end)}</div>
    <div><b>Scope</b>${escapeHtml(empLabel)}</div>
    <div><b>Records</b>${attendanceRecords.length}</div>
  </div>
  <div class="grand">
    <div><b>${grandHrs.toFixed(2)}</b> total hours</div>
    <div><b>${grandOT.toFixed(2)}</b> overtime hours</div>
    <div><b>${Object.keys(byEmp).length}</b> ${Object.keys(byEmp).length === 1 ? "employee" : "employees"}</div>
  </div>
  ${empSections.join("")}
  <div class="no-print" style="margin-top:30px;text-align:center">
    <button onclick="window.print()" style="padding:8px 18px;font-size:14px;cursor:pointer">Print</button>
  </div>
</body>
</html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 350);
  };
  const [clockOutVal, setClockOutVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // "Add record for past day" dialog
  const todayIso = () => new Date().toISOString().split("T")[0];
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    employee_id: "",
    date: todayIso(),
    clock_in: "07:30",
    clock_out: "17:00",
    is_leave: false,
    leave_type: "",
  });

  const openAdd = () => {
    setAddForm({
      employee_id: activeEmployees[0]?.id || "",
      date: todayIso(),
      clock_in: "07:30",
      clock_out: "17:00",
      is_leave: false,
      leave_type: "",
    });
    setAddOpen(true);
  };

  const handleSaveAdd = async () => {
    if (!addForm.employee_id) { toast.error("Pick an employee"); return; }
    if (!addForm.date) { toast.error("Pick a date"); return; }
    if (!addForm.is_leave) {
      if (addForm.clock_in && !/^\d{2}:\d{2}$/.test(addForm.clock_in)) { toast.error("Clock In must be HH:MM"); return; }
      if (addForm.clock_out && !/^\d{2}:\d{2}$/.test(addForm.clock_out)) { toast.error("Clock Out must be HH:MM"); return; }
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/employees/attendance/record`, {
        employee_id: addForm.employee_id,
        date: addForm.date,
        clock_in: addForm.is_leave ? null : addForm.clock_in,
        clock_out: addForm.is_leave ? null : addForm.clock_out,
        is_leave: addForm.is_leave,
        leave_type: addForm.is_leave ? (addForm.leave_type || "annual") : null,
      });
      toast.success(res.data?.created ? "Record added" : "Record updated");
      setAddOpen(false);
      if (refreshAttendance) refreshAttendance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (record) => {
    setEditing(record);
    setClockInVal(record.clock_in || "");
    setClockOutVal(record.clock_out || "");
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (clockInVal && !/^\d{2}:\d{2}$/.test(clockInVal)) {
      toast.error("Clock In must be HH:MM");
      return;
    }
    if (clockOutVal && !/^\d{2}:\d{2}$/.test(clockOutVal)) {
      toast.error("Clock Out must be HH:MM");
      return;
    }
    setBusy(true);
    try {
      await axios.put(`${API}/employees/attendance/${editing.id}`, {
        clock_in: clockInVal,
        clock_out: clockOutVal,
      });
      toast.success("Attendance updated");
      setEditOpen(false);
      setEditing(null);
      if (refreshAttendance) refreshAttendance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/employees/attendance/${confirmDelete.id}`);
      toast.success("Attendance record deleted");
      setConfirmDelete(null);
      if (refreshAttendance) refreshAttendance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Clock In/Out</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {activeEmployees.map((emp) => {
              const todayRecord = attendanceRecords.find(r =>
                r.employee_id === emp.id && r.date === new Date().toISOString().split("T")[0]
              );
              const clockedIn = todayRecord?.clock_in && !todayRecord?.clock_out;

              return (
                <Card key={emp.id} className="p-4" data-testid={`attendance-card-${emp.id}`}>
                  <div className="text-center">
                    <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                    <p className="text-xs text-gray-500 mb-2">{emp.position}</p>
                    {todayRecord?.clock_in && (
                      <p className="text-xs text-green-600 mb-1">In: {todayRecord.clock_in}</p>
                    )}
                    {todayRecord?.clock_out && (
                      <p className="text-xs text-red-600 mb-1">Out: {todayRecord.clock_out}</p>
                    )}
                    <div className="flex gap-2 justify-center mt-2">
                      <Button
                        size="sm"
                        variant={clockedIn ? "outline" : "default"}
                        onClick={() => onClockIn(emp.id)}
                        disabled={!!todayRecord?.clock_in}
                        className="h-7 text-xs"
                        data-testid={`clock-in-${emp.id}`}
                      >
                        <UserCheck className="h-3 w-3 mr-1" /> In
                      </Button>
                      <Button
                        size="sm"
                        variant={clockedIn ? "destructive" : "outline"}
                        onClick={() => onClockOut(emp.id)}
                        disabled={!clockedIn}
                        className="h-7 text-xs"
                        data-testid={`clock-out-${emp.id}`}
                      >
                        <UserX className="h-3 w-3 mr-1" /> Out
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Attendance Records</CardTitle>
          <Button
            size="sm"
            onClick={openAdd}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-attendance-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add record for past day
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div>
              <Label>From</Label>
              <Input
                type="date"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
                data-testid="attendance-start-date"
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="date"
                value={attendanceEndDate || attendanceDate}
                onChange={(e) => setAttendanceEndDate && setAttendanceEndDate(e.target.value)}
                data-testid="attendance-end-date"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Employee</Label>
              <Select value={attendanceEmployee} onValueChange={setAttendanceEmployee}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {[...activeEmployees].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick range presets */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const t = new Date().toISOString().split("T")[0];
                setAttendanceDate(t);
                setAttendanceEndDate && setAttendanceEndDate(t);
              }}
              data-testid="attendance-range-today"
            >
              Today
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const now = new Date();
                const day = now.getDay() || 7; // Mon=1..Sun=7
                const monday = new Date(now);
                monday.setDate(now.getDate() - (day - 1));
                const sunday = new Date(monday);
                sunday.setDate(monday.getDate() + 6);
                setAttendanceDate(monday.toISOString().split("T")[0]);
                setAttendanceEndDate && setAttendanceEndDate(sunday.toISOString().split("T")[0]);
              }}
              data-testid="attendance-range-week"
            >
              This week
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const now = new Date();
                const first = new Date(now.getFullYear(), now.getMonth(), 1);
                const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                setAttendanceDate(first.toISOString().split("T")[0]);
                setAttendanceEndDate && setAttendanceEndDate(last.toISOString().split("T")[0]);
              }}
              data-testid="attendance-range-month"
            >
              This month
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const now = new Date();
                const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const last = new Date(now.getFullYear(), now.getMonth(), 0);
                setAttendanceDate(first.toISOString().split("T")[0]);
                setAttendanceEndDate && setAttendanceEndDate(last.toISOString().split("T")[0]);
              }}
              data-testid="attendance-range-last-month"
            >
              Last month
            </Button>
          </div>

          {/* Totals summary */}
          {(() => {
            const totals = attendanceRecords.reduce((acc, r) => {
              acc.hours += Number(r.hours_worked || 0);
              acc.overtime += Number(r.overtime_hours || 0);
              acc.days += r.clock_in ? 1 : 0;
              const eid = r.employee_id;
              if (!acc.byEmp[eid]) {
                acc.byEmp[eid] = { hours: 0, overtime: 0, days: 0 };
              }
              acc.byEmp[eid].hours += Number(r.hours_worked || 0);
              acc.byEmp[eid].overtime += Number(r.overtime_hours || 0);
              acc.byEmp[eid].days += r.clock_in ? 1 : 0;
              return acc;
            }, { hours: 0, overtime: 0, days: 0, byEmp: {} });

            const empIds = Object.keys(totals.byEmp);
            const showPerEmp = (!attendanceEmployee || attendanceEmployee === "all") && empIds.length > 1;

            return (
              <div className="rounded-md border border-purple-200 bg-purple-50 p-3 mb-4" data-testid="attendance-totals">
                <div className="flex flex-wrap gap-4 items-baseline justify-between">
                  <div className="flex flex-wrap gap-4 items-baseline">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-purple-700">Total hours</div>
                      <div className="text-2xl font-bold text-purple-900" data-testid="attendance-total-hours">
                        {totals.hours.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-purple-700">Overtime</div>
                      <div className="text-xl font-semibold text-purple-900">{totals.overtime.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-purple-700">Days worked</div>
                      <div className="text-xl font-semibold text-purple-900">{totals.days}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-purple-700">Records</div>
                      <div className="text-xl font-semibold text-purple-900">{attendanceRecords.length}</div>
                    </div>
                  </div>
                  <Button
                    onClick={handlePrintTimesheet}
                    className="bg-purple-700 hover:bg-purple-800 text-white"
                    data-testid="print-timesheet-btn"
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    Print timesheet
                  </Button>
                </div>
                {showPerEmp && (
                  <div className="mt-3 pt-3 border-t border-purple-200">
                    <div className="text-[11px] uppercase tracking-wide text-purple-700 mb-1">Per employee</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 text-sm">
                      {empIds
                        .map(id => ({ id, ...totals.byEmp[id], name: getEmployeeName(id) }))
                        .sort((a, b) => b.hours - a.hours)
                        .map(emp => (
                          <div key={emp.id} className="flex justify-between bg-white/60 rounded px-2 py-1">
                            <span className="truncate pr-2">{emp.name}</span>
                            <span className="font-mono font-semibold">{emp.hours.toFixed(2)}h</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Overtime</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendanceRecords.map((record) => (
                <TableRow key={record.id || `${record.employee_id}-${record.date}`}>
                  <TableCell>{getEmployeeName(record.employee_id)}</TableCell>
                  <TableCell>{record.date}</TableCell>
                  <TableCell>{record.clock_in || "-"}</TableCell>
                  <TableCell>{record.clock_out || "-"}</TableCell>
                  <TableCell>{record.hours_worked?.toFixed(2) || "0"}</TableCell>
                  <TableCell>{record.overtime_hours?.toFixed(2) || "0"}</TableCell>
                  <TableCell>
                    {record.is_leave ? (
                      <span className="text-yellow-600">{record.leave_type} leave</span>
                    ) : record.clock_out ? (
                      <span className="text-green-600">Complete</span>
                    ) : record.clock_in ? (
                      <span className="text-blue-600">Working</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() => openEdit(record)}
                        data-testid={`edit-attendance-${record.id}`}
                        title="Edit times"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => setConfirmDelete(record)}
                        data-testid={`delete-attendance-${record.id}`}
                        title="Delete record"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {attendanceRecords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500">
                    No attendance records in this range
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-attendance-dialog">
          <DialogHeader>
            <DialogTitle>Edit Attendance Times</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="text-sm bg-gray-50 rounded p-2">
                <strong>{getEmployeeName(editing.employee_id)}</strong> · {editing.date}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block">Clock In (HH:MM, 24-hour)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="07:30"
                    pattern="[0-9]{2}:[0-9]{2}"
                    maxLength={5}
                    value={clockInVal}
                    onChange={(e) => setClockInVal(e.target.value)}
                    data-testid="edit-clock-in"
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Clock Out (HH:MM, 24-hour)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="17:30"
                    pattern="[0-9]{2}:[0-9]{2}"
                    maxLength={5}
                    value={clockOutVal}
                    onChange={(e) => setClockOutVal(e.target.value)}
                    data-testid="edit-clock-out"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Hours and overtime will be recalculated automatically. Format: <strong>HH:MM</strong> in 24-hour time (e.g. 07:30 or 17:00). Leave a field blank to clear it.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={busy} data-testid="save-attendance-edit">
              {busy ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Record dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-attendance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Add / replace attendance record
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1 block">Employee</Label>
              <Select
                value={addForm.employee_id}
                onValueChange={(v) => setAddForm({ ...addForm, employee_id: v })}
              >
                <SelectTrigger data-testid="add-att-employee">
                  <SelectValue placeholder="Pick an employee" />
                </SelectTrigger>
                <SelectContent>
                  {activeEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block">Date</Label>
              <Input
                type="date"
                value={addForm.date}
                max={todayIso()}
                onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                data-testid="add-att-date"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Any past or current day. Re-saving the same day overwrites existing record.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={addForm.is_leave}
                onChange={(e) => setAddForm({ ...addForm, is_leave: e.target.checked })}
                data-testid="add-att-leave"
              />
              Mark as leave day (no working hours)
            </label>

            {addForm.is_leave ? (
              <div>
                <Label className="mb-1 block">Leave type</Label>
                <Select
                  value={addForm.leave_type || "annual"}
                  onValueChange={(v) => setAddForm({ ...addForm, leave_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="family">Family responsibility</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="public_holiday">Public holiday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block">Clock In (HH:MM)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{2}:[0-9]{2}"
                    maxLength={5}
                    placeholder="07:30"
                    value={addForm.clock_in}
                    onChange={(e) => setAddForm({ ...addForm, clock_in: e.target.value })}
                    data-testid="add-att-clock-in"
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Clock Out (HH:MM)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{2}:[0-9]{2}"
                    maxLength={5}
                    placeholder="17:00"
                    value={addForm.clock_out}
                    onChange={(e) => setAddForm({ ...addForm, clock_out: e.target.value })}
                    data-testid="add-att-clock-out"
                  />
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500">
              Hours + overtime auto-calculated from the times you enter (>8h/day = OT). 24-hour format.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={handleSaveAdd}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-add-attendance"
            >
              {busy ? "Saving..." : "Save record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md" data-testid="delete-attendance-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete Attendance Record</DialogTitle>
          </DialogHeader>
          {confirmDelete && (
            <div className="py-2">
              <p className="text-sm">
                Delete the attendance record for <strong>{getEmployeeName(confirmDelete.employee_id)}</strong> on{" "}
                <strong>{confirmDelete.date}</strong>?
              </p>
              <p className="text-xs text-gray-500 mt-2">This cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={busy}
              data-testid="confirm-delete-attendance"
            >
              {busy ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
